import { useState, useEffect, useMemo, useCallback } from "react";
import {
  User,
  Task,
  Project,
  WorkLogEntry,
  Material,
  ProjectAssignment,
  ProjectFeedback,
} from "../types";
import {
  mockUsers,
  mockProjects,
  mockTasks,
  mockWorkLogs,
  mockMaterials,
} from "../data/mockData";
import { mapProjectsFromBackend } from "../utils/projectDataMapper";
import { mapTasksFromBackend } from "../utils/taskDataMapper";
import { mapUserDataFromBackend } from "../utils/userDataMapper";
import { mapMaterialFromBackend, mapMaterialsFromBackend } from "../utils/materialDataMapper";
import { mapWorkLogFromBackend, mapWorkLogsFromBackend } from "../utils/workLogDataMapper";
import { emailService } from "../utils/emailService";
import { apiService } from "../utils/apiService";

const FEEDBACK_STORAGE_KEY = "rio_project_feedback_entries";
const DATA_CACHE_KEY = "rio_cached_data";

const readPersistedFeedback = (): Record<string, ProjectFeedback[]> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const persistFeedbackForProject = (
  projectId: string,
  entries: ProjectFeedback[]
) => {
  if (typeof window === "undefined") return;
  const current = readPersistedFeedback();
  if (entries.length) {
    current[projectId] = entries;
  } else {
    delete current[projectId];
  }
  localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(current));
};

const mergePersistedFeedback = (
  project: Project,
  persisted: Record<string, ProjectFeedback[]>
): Project => {
  const stored = persisted[project.id] ?? [];
  if (!stored.length && !(project.feedbackEntries?.length)) {
    return project;
  }
  const combined = [...stored, ...(project.feedbackEntries ?? [])];
  const seen = new Set<string>();
  const deduped: ProjectFeedback[] = [];
  combined.forEach((entry) => {
    const dedupeKey =
      entry.id || `${project.id}-${entry.createdAt}-${entry.comment}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    deduped.push(entry);
  });
  return { ...project, feedbackEntries: deduped };
};

const readCachedData = (): Partial<{
  projects: Project[];
  tasks: Task[];
  workLogs: WorkLogEntry[];
  materials: Material[];
  users: User[];
}> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(DATA_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const writeCachedData = (
  snapshot: Partial<{
    projects: Project[];
    tasks: Task[];
    workLogs: WorkLogEntry[];
    materials: Material[];
    users: User[];
  }>
) => {
  if (typeof window === "undefined") return;
  try {
    const current = readCachedData();
    const merged = { ...current, ...snapshot };
    localStorage.setItem(DATA_CACHE_KEY, JSON.stringify(merged));
  } catch {}
};

function clampProgress(value: number) {
  return Math.min(100, Math.max(0, value));
}

export function useAppData(currentUser: User | null) {
  const initialCache = useMemo(() => readCachedData(), []);

  const [tasks, setTasks] = useState<Task[]>(initialCache.tasks ?? mockTasks);
  const [users, setUsers] = useState<User[]>(initialCache.users ?? mockUsers);
  const [projects, setProjects] = useState<Project[]>(
    initialCache.projects ?? mockProjects
  );
  const [workLogs, setWorkLogs] = useState<WorkLogEntry[]>(
    initialCache.workLogs ?? mockWorkLogs
  );
  const [materials, setMaterials] = useState<Material[]>(
    initialCache.materials ?? mockMaterials
  );
  const [backendHealthy, setBackendHealthy] = useState<boolean | null>(null);
  const [lastReloadAt, setLastReloadAt] = useState<number>(0);

  const aggregateProgressByProject = useCallback((logs: WorkLogEntry[]) => {
    return logs.reduce<Record<string, number>>((acc, log) => {
      const prev = acc[log.projectId] ?? 0;
      const next = clampProgress(prev + (log.progressPercentage ?? 0));
      acc[log.projectId] = next;
      return acc;
    }, {});
  }, []);

  const syncProjectProgressWithLogs = useCallback(
    (logs: WorkLogEntry[]) => {
      const aggregated = aggregateProgressByProject(logs);
      setProjects((prevProjects) =>
        prevProjects.map((project) => {
          const derived = aggregated[project.id];
          if (derived === undefined) return project;
          const nextProgress = Math.max(project.progress, derived);
          if (nextProgress === project.progress) return project;
          return { ...project, progress: nextProgress };
        })
      );
    },
    [aggregateProgressByProject]
  );

  const loadDataFromDatabase = useCallback(async () => {
    try {
      // Each request is isolated: e.g. clients are denied /users, which must not discard everything else
      const settle = <T,>(request: Promise<T>): Promise<T> =>
        request.catch((error) => {
          console.warn("Data request failed:", error);
          return {} as T;
        });

      const [projectsRes, tasksRes, workLogsRes, materialsRes, usersRes] =
        await Promise.all([
          settle(apiService.getProjects()),
          settle(apiService.getTasks()),
          settle(apiService.getWorkLogs()),
          settle(apiService.getMaterials()),
          settle(apiService.getUsers()),
        ]);

      const cacheSnapshot: Partial<{
        projects: Project[];
        tasks: Task[];
        workLogs: WorkLogEntry[];
        materials: Material[];
        users: User[];
      }> = {};

      if (projectsRes.data) {
        const persistedFeedback = readPersistedFeedback();
        const mappedProjects = mapProjectsFromBackend(projectsRes.data);
        const mergedProjects = mappedProjects.map((project) =>
          mergePersistedFeedback(project, persistedFeedback)
        );
        setProjects(mergedProjects);
        cacheSnapshot.projects = mergedProjects;
      }

      if (tasksRes.data) {
        const mappedTasks = mapTasksFromBackend(tasksRes.data);
        setTasks(mappedTasks);
        cacheSnapshot.tasks = mappedTasks;
      }

      if (workLogsRes.data) {
        const mappedLogs = mapWorkLogsFromBackend(workLogsRes.data);
        setWorkLogs(mappedLogs);
        syncProjectProgressWithLogs(mappedLogs);
        cacheSnapshot.workLogs = mappedLogs;
      }

      if (materialsRes.data) {
        const mappedMaterials = mapMaterialsFromBackend(materialsRes.data);
        setMaterials(mappedMaterials);
        cacheSnapshot.materials = mappedMaterials;
      }

      if (usersRes.data) {
        const mappedUsers = usersRes.data.map(mapUserDataFromBackend);
        setUsers(mappedUsers);
        cacheSnapshot.users = mappedUsers;
      }

      if (Object.keys(cacheSnapshot).length) {
        writeCachedData(cacheSnapshot);
      }
    } catch (error) {
      console.error("Failed to load data from database:", error);
    }
  }, [syncProjectProgressWithLogs]);

  // Initial load
  useEffect(() => {
    const initializeAppData = async () => {
      try {
        const healthCheck = await apiService.healthCheck();
        if (healthCheck.error) {
          setBackendHealthy(false);
          return;
        }

        setBackendHealthy(true);
        if (currentUser) {
          await loadDataFromDatabase();
        }
      } catch (error) {
        console.error("Failed to initialize app data:", error);
        setBackendHealthy(false);
      }
    };

    initializeAppData();
  }, [currentUser, loadDataFromDatabase]);

  // Periodically check backend health and reload
  useEffect(() => {
    let isMounted = true;
    const interval = setInterval(async () => {
      try {
        const res = await apiService.healthCheck();
        const healthy = !res.error;
        if (!isMounted) return;
        setBackendHealthy((prev) => {
          if (prev === false && healthy && currentUser) {
            loadDataFromDatabase()
              .then(() => setLastReloadAt(Date.now()))
              .catch(() => {});
          }
          return healthy;
        });
      } catch {
        if (!isMounted) return;
        setBackendHealthy(false);
      }
    }, 20000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [currentUser, loadDataFromDatabase]);

  // Refresh on tab visibility / online
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && backendHealthy && currentUser) {
        if (Date.now() - lastReloadAt > 10000) {
          loadDataFromDatabase()
            .then(() => setLastReloadAt(Date.now()))
            .catch(() => {});
        }
      }
    };

    const handleOnline = () => {
      setTimeout(async () => {
        const res = await apiService.healthCheck();
        const healthy = !res.error;
        setBackendHealthy(healthy);
        if (healthy && currentUser) {
          loadDataFromDatabase()
            .then(() => setLastReloadAt(Date.now()))
            .catch(() => {});
        }
      }, 1000);
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
    };
  }, [backendHealthy, currentUser, lastReloadAt, loadDataFromDatabase]);

  // Project handlers
  const handleCreateProject = async (
    projectData: Omit<Project, "id">
  ): Promise<Project> => {
    try {
      const response = await apiService.createProject(projectData);
      if (response.data) {
        const mapped = mapProjectsFromBackend([response.data])[0];
        setProjects((prevProjects) => [mapped, ...prevProjects]);
        return mapped;
      } else {
        throw new Error(response.error || "Failed to create project");
      }
    } catch (error) {
      console.error("Failed to create project:", error);
      const newProject: Project = {
        ...projectData,
        id: `project-${Date.now()}`,
      };
      setProjects((prevProjects) => [newProject, ...prevProjects]);
      return newProject;
    }
  };

  const handleUpdateProject = async (updatedProject: Project) => {
    setProjects((prevProjects) =>
      prevProjects.map((p) => (p.id === updatedProject.id ? updatedProject : p))
    );
    persistFeedbackForProject(
      updatedProject.id,
      updatedProject.feedbackEntries ?? []
    );

    try {
      await apiService.updateProject(updatedProject.id, updatedProject);
    } catch (e) {
      console.warn("Failed to persist project update:", e);
    }

    if (currentUser) {
      emailService.sendProjectUpdate(
        updatedProject,
        users,
        "progress_update",
        currentUser
      );
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      const response = await apiService.deleteProject(projectId);
      if (!response.error) {
        setProjects((prevProjects) =>
          prevProjects.filter((p) => p.id !== projectId)
        );
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
      setProjects((prevProjects) =>
        prevProjects.filter((p) => p.id !== projectId)
      );
    }
  };

  const handleAssignFabricator = (
    projectId: string,
    fabricatorId: string,
    message?: string
  ) => {
    const project = projects.find((p) => p.id === projectId);
    const fabricator = users.find((u) => u.id === fabricatorId);
    const supervisor = currentUser;

    if (!project || !fabricator || !supervisor) return;

    const newAssignment: ProjectAssignment = {
      id: `pa-${Date.now()}`,
      projectId,
      fabricatorId,
      assignedBy: currentUser!.id,
      assignedAt: new Date().toISOString(),
      status: "pending",
      message,
    };

    const updatedPendingAssignments = [
      ...(project.pendingAssignments || []),
      newAssignment,
    ];
    const updatedStatus: Project["status"] = "pending-assignment";

    setProjects((prevProjects) =>
      prevProjects.map((p) =>
        p.id === projectId
          ? {
              ...p,
              pendingAssignments: updatedPendingAssignments,
              status: updatedStatus,
            }
          : p
      )
    );

    apiService
      .updateProject(projectId, {
        pendingAssignments: updatedPendingAssignments,
        status: updatedStatus,
      })
      .catch((error) => {
        console.warn("Failed to persist fabricator assignment.", error);
      });

    emailService.sendProjectAssignment(
      newAssignment,
      project,
      fabricator,
      supervisor
    );

    const projectTasks: Omit<Task, "id" | "createdAt" | "updatedAt">[] = [
      {
        projectId,
        title: "Project Planning Review",
        description: "Review project requirements and create detailed work plan",
        status: "pending",
        priority: "high",
        assignedTo: fabricatorId,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        estimatedHours: 8,
        actualHours: 0,
        createdBy: currentUser.id,
      },
      {
        projectId,
        title: "Material Assessment",
        description: "Assess and order required materials for the project",
        status: "pending",
        priority: "medium",
        assignedTo: fabricatorId,
        dueDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        estimatedHours: 4,
        actualHours: 0,
        createdBy: currentUser.id,
      },
    ];

    const newTasks = projectTasks.map((taskData) => ({
      ...taskData,
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    setTasks((prevTasks) => [...prevTasks, ...newTasks]);
  };

  const handleBroadcastFabricators = async (
    projectId: string,
    message?: string
  ) => {
    try {
      const response = await apiService.broadcastToFabricators(
        projectId,
        message
      );
      if (response.data?.assignments) {
        setProjects((prev) =>
          prev.map((p) =>
            p.id === projectId
              ? {
                  ...p,
                  pendingAssignments: response.data.assignments,
                  status: "pending-assignment",
                }
              : p
          )
        );
      }
    } catch (error) {
      console.error("Broadcast failed", error);
    }
  };

  const handleAcceptAssignment = async (
    assignmentId: string,
    response?: string,
    projectId?: string
  ) => {
    const targetProjectId =
      projectId ||
      (assignmentId
        ? projects.find((p) =>
            p.pendingAssignments?.some((a) => a.id === assignmentId)
          )?.id
        : null);

    if (!targetProjectId) return;

    try {
      await apiService.respondToAssignment(
        targetProjectId,
        "accepted",
        assignmentId || undefined
      );

      setProjects((prevProjects) =>
        prevProjects.map((project) => {
          if (project.id !== targetProjectId) return project;

          if (
            currentUser?.role === "supervisor" &&
            project.pendingSupervisors?.includes(currentUser.id)
          ) {
            return {
              ...project,
              supervisorId: currentUser.id,
              pendingSupervisors: project.pendingSupervisors.filter(
                (id) => id !== currentUser.id
              ),
              status: "planning",
            };
          }

          return {
            ...project,
            pendingAssignments: project.pendingAssignments?.map((assignment) =>
              assignment.id === assignmentId
                ? {
                    ...assignment,
                    status: "accepted" as const,
                    response,
                    respondedAt: new Date().toISOString(),
                  }
                : assignment
            ),
            fabricatorIds: project.pendingAssignments?.find(
              (a) => a.id === assignmentId && a.status === "pending"
            )
              ? [
                  ...project.fabricatorIds,
                  project.pendingAssignments.find((a) => a.id === assignmentId)!
                    .fabricatorId,
                ]
              : project.fabricatorIds,
            status: project.pendingAssignments?.find(
              (a) => a.id === assignmentId && a.status === "pending"
            )
              ? ("planning" as const)
              : project.status,
          };
        })
      );
    } catch (error) {
      console.error("Failed to accept assignment", error);
    }
  };

  const handleDeclineAssignment = async (
    assignmentId: string,
    response?: string,
    projectId?: string
  ) => {
    const targetProjectId =
      projectId ||
      (assignmentId
        ? projects.find((p) =>
            p.pendingAssignments?.some((a) => a.id === assignmentId)
          )?.id
        : null);
    if (!targetProjectId) return;

    try {
      await apiService.respondToAssignment(
        targetProjectId,
        "declined",
        assignmentId || undefined
      );

      setProjects((prevProjects) =>
        prevProjects.map((project) => {
          if (project.id !== targetProjectId) return project;

          if (
            currentUser?.role === "supervisor" &&
            project.pendingSupervisors?.includes(currentUser.id)
          ) {
            return {
              ...project,
              pendingSupervisors: project.pendingSupervisors.filter(
                (id) => id !== currentUser.id
              ),
            };
          }

          return {
            ...project,
            pendingAssignments: project.pendingAssignments?.map((assignment) =>
              assignment.id === assignmentId
                ? {
                    ...assignment,
                    status: "declined" as const,
                    response,
                    respondedAt: new Date().toISOString(),
                  }
                : assignment
            ),
          };
        })
      );
    } catch (error) {
      console.error("Failed to decline assignment", error);
    }
  };

  // Task handlers
  const handleUpdateTaskStatus = async (
    taskId: string,
    status: Task["status"]
  ) => {
    const previousTask = tasks.find((task) => task.id === taskId);
    const updatedAt = new Date().toISOString();

    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId ? { ...task, status, updatedAt } : task
      )
    );

    try {
      const { data } = await apiService.updateTask(taskId, { status, updatedAt });
      if (data) {
        setTasks((prevTasks) =>
          prevTasks.map((task) => (task.id === taskId ? { ...task, ...data } : task))
        );
      }
    } catch (error) {
      console.error("Failed to update task status:", error);
      if (previousTask) {
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.id === taskId ? { ...task, ...previousTask } : task
          )
        );
      }
    }
  };

  const handleCreateTask = (
    taskData: Omit<Task, "id" | "createdAt" | "updatedAt">
  ) => {
    const newTask: Task = {
      ...taskData,
      id: `task-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setTasks((prevTasks) => [newTask, ...prevTasks]);
  };

  const handleUpdateTask = (taskId: string, updates: Partial<Task>) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.id === taskId
          ? { ...task, ...updates, updatedAt: new Date().toISOString() }
          : task
      )
    );
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const { error } = await apiService.deleteTask(taskId);
      if (error) {
        console.error("Delete failed:", error);
        return;
      }
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
    } catch (err) {
      console.error("An unexpected error occurred during deletion:", err);
    }
  };

  // Worklog handlers
  const handleAddWorkLog = async (
    workLogData: Omit<WorkLogEntry, "id" | "createdAt">
  ) => {
    try {
      const response = await apiService.createWorkLog(workLogData);
      if (response.error) {
        console.error("Failed to save work log:", response.error, response.data);
        return;
      }

      const savedLog = response.data ? mapWorkLogFromBackend(response.data) : null;
      if (!savedLog) {
        console.error("Server responded but returned no data.");
        return;
      }

      setWorkLogs((prevLogs) => {
        const nextLogs = [savedLog, ...prevLogs];
        syncProjectProgressWithLogs(nextLogs);
        return nextLogs;
      });

      const progressIncrease = Number.isFinite(savedLog.progressPercentage)
        ? savedLog.progressPercentage
        : 0;
      let updatedProjectSnapshot: Project | null = null;
      setProjects((prevProjects) =>
        prevProjects.map((project) => {
          if (project.id !== savedLog.projectId) return project;
          const nextProgress = clampProgress(project.progress + progressIncrease);
          updatedProjectSnapshot = {
            ...project,
            progress: nextProgress,
          };
          return updatedProjectSnapshot;
        })
      );

      if (updatedProjectSnapshot) {
        handleUpdateProject(updatedProjectSnapshot);
      }
    } catch (error) {
      console.error("Network crash:", error);
      alert("Network Error: Could not save work log.");
    }
  };

  const handleUpdateWorkLog = async (
    id: string,
    updates: Partial<WorkLogEntry>
  ) => {
    const previousLog = workLogs.find((log) => log.id === id);
    if (!previousLog) return;

    const previousLogs = workLogs;
    const previousProjectProgress =
      projects.find((project) => project.id === previousLog.projectId)?.progress ??
      0;

    const normalizedUpdates: Partial<WorkLogEntry> = { ...updates };
    if (typeof updates.progressPercentage === "number") {
      normalizedUpdates.progressPercentage = clampProgress(
        updates.progressPercentage
      );
    }
    if (typeof updates.hoursWorked === "number") {
      normalizedUpdates.hoursWorked = Math.max(0, updates.hoursWorked);
    }

    const nextProgressValue =
      typeof normalizedUpdates.progressPercentage === "number"
        ? normalizedUpdates.progressPercentage
        : previousLog.progressPercentage;
    const progressDelta =
      typeof normalizedUpdates.progressPercentage === "number"
        ? nextProgressValue - previousLog.progressPercentage
        : 0;
    const nextProjectProgress =
      progressDelta !== 0
        ? clampProgress(previousProjectProgress + progressDelta)
        : previousProjectProgress;

    const nextMaterials = Array.isArray(normalizedUpdates.materials)
      ? normalizedUpdates.materials
      : previousLog.materials;

    const updatedLog: WorkLogEntry = {
      ...previousLog,
      ...normalizedUpdates,
      materials:
        Array.isArray(nextMaterials) && nextMaterials.length > 0
          ? nextMaterials
          : undefined,
    };

    setWorkLogs((prevLogs) =>
      prevLogs.map((log) => (log.id === id ? updatedLog : log))
    );

    if (progressDelta !== 0) {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === previousLog.projectId
            ? { ...project, progress: nextProjectProgress }
            : project
        )
      );
    }

    try {
      const response = await apiService.updateWorkLog(id, {
        date: normalizedUpdates.date,
        description: normalizedUpdates.description,
        progressPercentage: normalizedUpdates.progressPercentage,
        hoursWorked: normalizedUpdates.hoursWorked,
        materials: normalizedUpdates.materials,
      });

      if (response.data) {
        const mapped = mapWorkLogFromBackend(response.data);
        setWorkLogs((prevLogs) =>
          prevLogs.map((log) => (log.id === id ? mapped : log))
        );
      }
    } catch (error) {
      console.error("Failed to update work log:", error);
      setWorkLogs(previousLogs);
      if (progressDelta !== 0) {
        setProjects((prevProjects) =>
          prevProjects.map((project) =>
            project.id === previousLog.projectId
              ? { ...project, progress: previousProjectProgress }
              : project
          )
        );
      }
      alert("Failed to update work log.");
    }
  };

  const handleDeleteWorkLog = async (id: string) => {
    const previousLog = workLogs.find((log) => log.id === id);
    if (!previousLog) return;

    const previousLogs = workLogs;
    const previousProjectProgress =
      projects.find((project) => project.id === previousLog.projectId)?.progress ??
      0;
    const progressDelta = Number.isFinite(previousLog.progressPercentage)
      ? -previousLog.progressPercentage
      : 0;
    const nextProjectProgress =
      progressDelta !== 0
        ? clampProgress(previousProjectProgress + progressDelta)
        : previousProjectProgress;

    setWorkLogs((prevLogs) => prevLogs.filter((log) => log.id !== id));

    if (progressDelta !== 0) {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === previousLog.projectId
            ? { ...project, progress: nextProjectProgress }
            : project
        )
      );
    }

    try {
      await apiService.deleteWorkLog(id);
    } catch (error) {
      console.error("Failed to delete work log:", error);
      setWorkLogs(previousLogs);
      if (progressDelta !== 0) {
        setProjects((prevProjects) =>
          prevProjects.map((project) =>
            project.id === previousLog.projectId
              ? { ...project, progress: previousProjectProgress }
              : project
          )
        );
      }
      alert("Failed to delete work log.");
    }
  };

  // Material handlers
  const handleAddMaterial = async (
    materialData: Omit<Material, "id" | "addedAt">
  ) => {
    try {
      const payload = {
        ...materialData,
        projectId: materialData.projectId ?? "general",
        costPerUnit: materialData.cost,
        totalCost: materialData.cost * materialData.quantity,
      };
      const response = await apiService.createMaterial(payload);

      if (response.data) {
        const mapped = mapMaterialFromBackend(response.data);
        setMaterials((prevMaterials) => [mapped, ...prevMaterials]);
        return;
      }
      throw new Error(response.error || "Failed to save material");
    } catch (error) {
      console.error("Failed to save material:", error);
      alert("Failed to save material. It will only be stored locally.");
      const newMaterial: Material = {
        ...materialData,
        id: `mat-${Date.now()}`,
        addedAt: new Date().toISOString(),
      };
      setMaterials((prevMaterials) => [newMaterial, ...prevMaterials]);
    }
  };

  const handleUpdateMaterial = async (
    id: string,
    updates: Partial<Material>
  ) => {
    try {
      const response = await apiService.updateMaterial(id, updates);
      if (response.data) {
        const updatedMaterial = mapMaterialFromBackend(response.data);
        setMaterials((prevMaterials) =>
          prevMaterials.map((m) => (m.id === id ? updatedMaterial : m))
        );
      } else {
        console.error("Failed to update material:", response.error);
      }
    } catch (error) {
      console.error("Error updating material:", error);
    }
  };

  const handleDeleteMaterial = async (id: string) => {
    try {
      const response = await apiService.deleteMaterial(id);
      if (!response.error) {
        setMaterials((prevMaterials) =>
          prevMaterials.filter((m) => m.id !== id)
        );
      } else {
        console.error("Failed to delete material:", response.error);
      }
    } catch (error) {
      console.error("Error deleting material:", error);
    }
  };

  // User management helper (adding user without overriding current user)
  const handleAddUser = (newUser: User) => {
    setUsers((prevUsers) => {
      const exists = prevUsers.some((user) => user.id === newUser.id);
      if (exists) {
        return prevUsers.map((user) =>
          user.id === newUser.id ? newUser : user
        );
      }
      return [...prevUsers, newUser];
    });
  };

  return {
    tasks,
    users,
    projects,
    workLogs,
    materials,
    backendHealthy,
    setUsers,
    loadDataFromDatabase,
    handleCreateProject,
    handleUpdateProject,
    handleDeleteProject,
    handleAssignFabricator,
    handleBroadcastFabricators,
    handleAcceptAssignment,
    handleDeclineAssignment,
    handleUpdateTaskStatus,
    handleCreateTask,
    handleUpdateTask,
    handleDeleteTask,
    handleAddWorkLog,
    handleUpdateWorkLog,
    handleDeleteWorkLog,
    handleAddMaterial,
    handleUpdateMaterial,
    handleDeleteMaterial,
    handleAddUser,
  };
}
